import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import logo from '../assets/logo.svg';
import { authApi, employeeApi, getErrorMessage } from '../utils/api';
import { validateUsername, validatePassword } from '../utils/validation';
import { setAuthTokens, setUserData, isAuthenticated } from '../utils/auth';
import { ROUTES } from '../constants/app';
import NavigationModal from './NavigationModal';

function LoginPage() {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(ROUTES.START);
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (error) setError('');
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    const usernameValidation = validateUsername(credentials.username);
    if (!usernameValidation.isValid) {
      errors.username = usernameValidation.error;
    }
    
    const passwordValidation = validatePassword(credentials.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate form inputs
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the authentication API
      const response = await authApi.login({
        name: credentials.username,
        password: credentials.password
      });
      
      if (response.success && response.data) {
        // Store authentication data from the server response
        if (response.data.tokens) {
          setAuthTokens(response.data.tokens);
        }
        
        if (response.data.admin) {
          setUserData(response.data.admin);
        }
        
        // Show navigation modal instead of direct redirect
        setShowNavigationModal(true);
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLogin(e);
    }
  };

  const handleCloseNavigationModal = () => {
    setShowNavigationModal(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="logo-section">
          <img src={logo} alt="Exchange Logo" className="logo-image" />
        </div>
        
        <div className="login-form-section">
          <h1 className="login-title">Kiosk Sign In</h1>
          
          <form className="login-form" onSubmit={handleLogin}>
            <div className="input-group">
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className={`login-input ${validationErrors.username ? 'error' : ''}`}
                placeholder="Enter your username"
                disabled={loading}
                autoComplete="username"
                aria-describedby={validationErrors.username ? 'username-error' : undefined}
              />
              {validationErrors.username && (
                <div id="username-error" className="field-error">
                  {validationErrors.username}
                </div>
              )}
            </div>
            
            <div className="input-group">
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className={`login-input ${validationErrors.password ? 'error' : ''}`}
                placeholder="Enter your password"
                disabled={loading}
                autoComplete="current-password"
                aria-describedby={validationErrors.password ? 'password-error' : undefined}
              />
              {validationErrors.password && (
                <div id="password-error" className="field-error">
                  {validationErrors.password}
                </div>
              )}
            </div>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <button 
              type="submit" 
              className="login-button" 
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
      
      <NavigationModal 
        isOpen={showNavigationModal} 
        onClose={handleCloseNavigationModal} 
      />
    </div>
  );
}

export default LoginPage;
